import { GLOSARIO, objetivoDeLaVista } from '@alexiarest/shared';
import {
  analyzePricing,
  benchmarkKpis,
  buildContext,
  cashAlerts,
  computeCashFlow,
  computeKpis,
  computeProfitAndLoss,
  computeRecipeCost,
  findCostDeviations,
  menuEngineering,
  monthlyProfitAndLoss,
  payables,
  projectCashFlow,
  weekdayPerformance,
} from '@alexiarest/shared';
import type {
  CashMovement,
  DailyRevenue,
  ExpenseEntry,
  FinanceInput,
  Ingredient,
  Investment,
  MenuItem,
  Period,
  PricingAnalysis,
  Recipe,
  RecipeCost,
  Target,
  TenantSettings,
} from '@alexiarest/shared';
import {
  activeCurrency,
  capitalize,
  formatDate,
  formatMoney,
  formatMonthLong,
  formatPct,
  safe,
} from '../format';
import type {
  AssistantChatResponse,
  AssistantSuggestion,
  AssistantToolCall,
} from './types';

/**
 * Alexia en modo demostracion.
 *
 * No hay modelo detras: ni sugerencias ni respuestas se inventan. Todo sale
 * del mismo motor de calculo que usa el resto de la app sobre el dataset de
 * ejemplo, asi que las cifras que se leen aqui son las mismas que se ven en
 * las pantallas de tesoreria, precios o cuenta de resultados.
 */

export interface DemoAssistantData {
  settings: TenantSettings;
  ingredients: Ingredient[];
  recipes: Recipe[];
  menuItems: MenuItem[];
  revenues: DailyRevenue[];
  expenses: ExpenseEntry[];
  investments: Investment[];
  cashMovements: CashMovement[];
  targets?: Target[];
}

/** Etiquetas de los indicadores en palabras de dueño (14 oct 2026). */
const ETIQUETA_DE_DUENO: Record<string, string> = {
  'Costo de materia prima': 'Materia prima',
  'Costo de personal': 'Personal',
  'Prime Cost': 'Materia prima + personal',
  EBITDA: 'Te dejó el negocio',
  'Margen de seguridad': 'Cuánto pueden bajar tus ventas',
};

/** Umbral de food cost a partir del cual la cosa pinta mal (benchmark del sector). */
const FOOD_COST_ALERT_PCT = 38;
const MAX_SUGGESTIONS = 5;

const tool = (name: string, summary: string, input?: unknown): AssistantToolCall => ({
  name,
  input,
  summary,
});

/* ------------------------------------------------------------------ */
/* Contexto de calculo                                                 */
/* ------------------------------------------------------------------ */

interface DemoContext {
  data: DemoAssistantData;
  period: Period;
  today: string;
  finance: FinanceInput;
  target: Target | null;
  /** Analisis de precios de la carta, saltando los platos mal configurados. */
  pricing: Array<{ item: MenuItem; analysis: PricingAnalysis; cost: RecipeCost }>;
}

function buildDemoContext(data: DemoAssistantData, period: Period, today: string): DemoContext {
  const costing = buildContext(data.ingredients, data.recipes);
  const pricing: DemoContext['pricing'] = [];
  for (const item of data.menuItems) {
    if (!item.active) continue;
    try {
      const cost = computeRecipeCost(item.recipeId, costing);
      pricing.push({ item, analysis: analyzePricing(item, cost, data.settings), cost });
    } catch {
      // Un plato mal configurado no puede tumbar la respuesta entera.
    }
  }

  return {
    data,
    period,
    today,
    finance: {
      revenues: data.revenues,
      expenses: data.expenses,
      investments: data.investments,
      cashMovements: data.cashMovements,
    },
    // Bloque I: objetivos para siempre (con recomendados), como la API.
    target: objetivoDeLaVista(data.targets ?? [], { tenantId: 'demo', month: period.end.slice(0, 7), locationIds: ['principal'] }),
    pricing,
  };
}

const periodInput = (ctx: DemoContext) => ({ start: ctx.period.start, end: ctx.period.end });

/* ------------------------------------------------------------------ */
/* Sugerencias                                                         */
/* ------------------------------------------------------------------ */

/** Preguntas de arranque cuando el restaurante todavia no tiene senyales claras. */
const GENERIC: AssistantSuggestion[] = [
  {
    question: '¿Cómo va el negocio en este periodo?',
    reason: 'Un repaso rápido a tus indicadores frente a tus objetivos.',
  },
  {
    question: '¿Qué platos de la carta me dejan más dinero?',
    reason: 'Qué mantener, qué recostear y qué sacar de la carta.',
  },
  {
    question: '¿Voy a tener problemas de caja?',
    reason: 'Proyección de tesorería a tres meses a partir de tu histórico.',
  },
];

export function demoSuggestions(
  data: DemoAssistantData,
  period: Period,
  today: string,
): AssistantSuggestion[] {
  const ctx = buildDemoContext(data, period, today);
  const items: AssistantSuggestion[] = [];

  /* --- Facturas vencidas: lo mas urgente, va primero ----------------- */
  const overdue = payables(data.expenses, today).filter((p) => p.status === 'vencida');
  if (overdue.length > 0) {
    const total = overdue.reduce((sum, p) => sum + safe(p.grossAmount), 0);
    const oldest = overdue.reduce((max, p) => Math.max(max, safe(p.daysOverdue)), 0);
    items.push({
      question:
        overdue.length === 1
          ? 'Tengo una factura vencida, ¿cuánto debo y a quién?'
          : `Tengo ${overdue.length} facturas vencidas, ¿cuánto debo y a quién?`,
      reason: `${formatMoney(total)} vencidos, la más antigua desde hace ${oldest} días.`,
    });
  }

  /* --- Caja proyectada en negativo ---------------------------------- */
  const months = computeCashFlow(ctx.finance, period, data.settings, 0);
  const negative = projectCashFlow(months, 3).find((m) => safe(m.closingBalance) < 0);
  const alerts = cashAlerts(months, activeCurrency());
  if (negative) {
    items.push({
      question: '¿Voy a tener problemas de caja?',
      reason: `Con el ritmo actual, en ${formatMonthLong(negative.month)} la caja se quedaría en ${formatMoney(negative.closingBalance)}.`,
    });
  } else if (alerts.length > 0) {
    items.push({ question: '¿Voy a tener problemas de caja?', reason: alerts[0] });
  }

  /* --- Coste de materia prima disparado ------------------------------ */
  const kpis = computeKpis(ctx.finance, period, data.settings);
  const foodCost = benchmarkKpis(kpis).find((b) => b.key === 'foodCostPct');
  if (
    safe(kpis.netRevenue) > 0 &&
    foodCost &&
    (foodCost.status === 'mal' || safe(kpis.foodCostPct) > FOOD_COST_ALERT_PCT)
  ) {
    items.push({
      question: '¿Por qué se me disparó la materia prima?',
      reason: `Tu materia prima está en ${formatPct(kpis.foodCostPct)} de lo que vendes; por encima del ${FOOD_COST_ALERT_PCT} % ya se come lo que te deja el negocio.`,
    });
  }

  /* --- EBITDA a la baja mes a mes ------------------------------------ */
  const monthly = monthlyProfitAndLoss(ctx.finance, period).filter((m) => safe(m.revenue) > 0);
  if (monthly.length >= 2) {
    const last = monthly[monthly.length - 1];
    const previous = monthly[monthly.length - 2];
    if (safe(last.ebitda) < safe(previous.ebitda)) {
      items.push({
        question: `¿Por qué me dejó menos el negocio en ${formatMonthLong(last.periodStart.slice(0, 7))}?`,
        reason: `Pasó de ${formatMoney(previous.ebitda)} a ${formatMoney(last.ebitda)} respecto al mes anterior.`,
      });
    }
  }

  /* --- Platos que pierden dinero en delivery ------------------------- */
  const platformDishes = ctx.pricing.filter((row) => row.analysis.platformId);
  const losing = platformDishes.filter((row) => safe(row.analysis.grossMargin) < 0);
  const thin = platformDishes.filter(
    (row) => safe(row.analysis.grossMarginPct) < safe(data.settings.alerts.minGrossMarginPct),
  );
  if (losing.length > 0) {
    const worst = losing[0].analysis;
    items.push({
      question: '¿Qué platos pierden dinero en delivery?',
      reason:
        losing.length === 1
          ? `«${worst.name}» deja ${formatMoney(worst.grossMargin)} de margen en ${worst.platformName ?? 'la plataforma'}.`
          : `${losing.length} platos tienen margen negativo con la comisión de la plataforma.`,
    });
  } else if (thin.length > 0) {
    const worst = thin[0].analysis;
    items.push({
      question: '¿Me sale a cuenta el delivery?',
      reason: `«${worst.name}» se queda en ${formatPct(worst.grossMarginPct)} de margen, por debajo de tu mínimo.`,
    });
  }

  /* --- Desviaciones de coste ---------------------------------------- */
  const deviations = findCostDeviations(
    ctx.pricing.map(({ item, cost }) => ({ item, cost })),
    data.settings,
  );
  if (deviations.length > 0) {
    const worst = deviations[0];
    items.push({
      question: '¿Qué platos tengo que recostear?',
      reason: `${deviations.length} platos se han desviado del costo de referencia; el peor, «${worst.name}», un ${formatPct(worst.deviationPct)}.`,
    });
  }

  /* --- Relleno hasta tener algo que ofrecer -------------------------- */
  for (const generic of GENERIC) {
    if (items.length >= 3) break;
    if (items.some((i) => i.question === generic.question)) continue;
    items.push(generic);
  }

  return items.slice(0, MAX_SUGGESTIONS);
}

/* ------------------------------------------------------------------ */
/* Respuestas del chat                                                 */
/* ------------------------------------------------------------------ */

interface Answer {
  reply: string;
  toolCalls: AssistantToolCall[];
}

/** Sin tildes ni mayusculas: asi «cuánto» y «cuanto» buscan lo mismo. */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const bullet = (lines: string[]): string => lines.map((line) => `- ${line}`).join('\n');

/* --- Facturas por pagar -------------------------------------------- */

function answerPayables(ctx: DemoContext): Answer {
  const items = payables(ctx.data.expenses, ctx.today);
  const overdue = items.filter((p) => p.status === 'vencida');
  const upcoming = items.filter((p) => p.status === 'por_vencer');
  const calls = [tool('get_payables', 'Facturas vencidas y por vencer', { today: ctx.today })];

  if (items.length === 0) {
    return {
      reply:
        'No tienes ninguna factura a crédito pendiente de pago. Todo lo que tienes registrado está marcado como pagado.',
      toolCalls: calls,
    };
  }

  const parts: string[] = [];
  const totalOverdue = overdue.reduce((sum, p) => sum + safe(p.grossAmount), 0);

  if (overdue.length > 0) {
    parts.push(
      `**Tienes ${overdue.length} ${overdue.length === 1 ? 'factura vencida' : 'facturas vencidas'} por ${formatMoney(totalOverdue)}.**`,
    );
    parts.push(
      bullet(
        overdue.map(
          (p) =>
            `**${p.concept}**${p.invoiceNumber ? ` (${p.invoiceNumber})` : ''}: ${formatMoney(p.grossAmount)}, vencía el ${formatDate(p.dueDate)} — ${safe(p.daysOverdue)} días de retraso.`,
        ),
      ),
    );
  } else {
    parts.push('**No tienes facturas vencidas.**');
  }

  if (upcoming.length > 0) {
    const totalUpcoming = upcoming.reduce((sum, p) => sum + safe(p.grossAmount), 0);
    parts.push(`Por vencer tienes ${formatMoney(totalUpcoming)}:`);
    parts.push(
      bullet(
        upcoming.map(
          (p) => `**${p.concept}**: ${formatMoney(p.grossAmount)}, vence el ${formatDate(p.dueDate)}.`,
        ),
      ),
    );
  }

  const total = items.reduce((sum, p) => sum + safe(p.grossAmount), 0);
  parts.push(`En total debes ${formatMoney(total)} a proveedores.`);
  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* --- Tesoreria ------------------------------------------------------ */

function answerCash(ctx: DemoContext): Answer {
  const months = computeCashFlow(ctx.finance, ctx.period, ctx.data.settings, 0);
  const calls = [tool('get_cashflow', 'Tesorería con alertas y proyección', periodInput(ctx))];

  if (months.length === 0) {
    return {
      reply: 'No hay movimientos de caja en el periodo seleccionado, así que no puedo proyectar nada.',
      toolCalls: calls,
    };
  }

  const last = months[months.length - 1];
  const projection = projectCashFlow(months, 3);
  const alerts = cashAlerts(months, activeCurrency());
  const parts: string[] = [
    `Cierras ${formatMonthLong(last.month)} con **${formatMoney(last.closingBalance)}** en caja. Descontando el IVA que todavía tienes que liquidar, te quedan ${formatMoney(last.balanceExcludingVat)} realmente disponibles.`,
  ];

  if (projection.length > 0) {
    parts.push('Proyección con el ritmo de los últimos meses:');
    parts.push(
      bullet(
        projection.map(
          (m) => `${capitalize(formatMonthLong(m.month))}: ${formatMoney(m.closingBalance)}`,
        ),
      ),
    );
  }

  const negative = projection.find((m) => safe(m.closingBalance) < 0);
  parts.push(
    negative
      ? `**Ojo:** en ${formatMonthLong(negative.month)} la caja se quedaría en negativo. Es el mes que hay que preparar.`
      : 'Con estas cifras no te quedas sin caja en los próximos tres meses.',
  );

  if (alerts.length > 0) {
    parts.push('Meses que ya han dado aviso:');
    parts.push(bullet(alerts));
  }

  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* --- Coste de materia prima ----------------------------------------- */

function answerFoodCost(ctx: DemoContext): Answer {
  const kpis = computeKpis(ctx.finance, ctx.period, ctx.data.settings);
  const pnl = computeProfitAndLoss(ctx.finance, ctx.period);
  const calls = [
    tool('get_kpis', 'KPIs del periodo frente a tus objetivos', periodInput(ctx)),
    tool('get_pnl', 'Estado de resultados del periodo', periodInput(ctx)),
  ];

  if (safe(kpis.netRevenue) <= 0) {
    return {
      reply: 'No hay ventas registradas en este periodo, así que no puedo calcular tu costo de materia prima.',
      toolCalls: calls,
    };
  }

  const value = safe(kpis.foodCostPct);
  const foodCostTarget = ctx.target?.foodCostPct;
  const verdict = foodCostTarget != null
    ? value <= foodCostTarget
      ? `Está **dentro de tu objetivo**, que has fijado en ${formatPct(foodCostTarget)}.`
      : `Está **por encima de tu objetivo** de ${formatPct(foodCostTarget)}.`
    : value <= 32
      ? 'Sin un objetivo configurado, está **dentro de la referencia general del sector** (hasta 32 %).'
      : value <= FOOD_COST_ALERT_PCT
        ? 'Sin un objetivo configurado, está **algo por encima de la referencia general del sector** (32 %). Todavía no es grave, pero conviene vigilarlo.'
        : `Sin un objetivo configurado, está **por encima del ${FOOD_COST_ALERT_PCT} %**, una referencia general a partir de la cual el costo de materia prima empieza a comerse el margen.`;

  const parts: string[] = [
    `Tu materia prima es **${formatPct(value)}** de lo que vendiste: ${formatMoney(pnl.materialCost)} de compras sobre ${formatMoney(pnl.netRevenue)} de ventas.`,
    verdict,
    `Junto al personal (${formatPct(kpis.laborCostPct)}), materia prima + personal suman **${formatPct(kpis.primeCostPct)}**.${ctx.target?.primeCostPct != null ? ` Tu objetivo es ${formatPct(ctx.target.primeCostPct)}.` : ' Como referencia general, se recomienda mantenerlo en el 60 % o menos.'}`,
  ];

  const deviations = findCostDeviations(
    ctx.pricing.map(({ item, cost }) => ({ item, cost })),
    ctx.data.settings,
  );
  if (deviations.length > 0) {
    calls.push(tool('get_menu_deviations', 'Platos con desviación de costo'));
    parts.push('Platos cuyo costo real se ha separado del que tenías previsto:');
    parts.push(
      bullet(
        deviations
          .slice(0, 5)
          .map(
            (d) =>
              `**${d.name}**: ${formatMoney(d.currentCost)} por porción frente a ${formatMoney(d.theoreticalCost)} previstos (${formatPct(d.deviationPct)}).`,
          ),
      ),
    );
  }

  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* --- Resultado mes a mes -------------------------------------------- */

const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** Si la pregunta nombra un mes ("¿por que bajo el EBITDA en junio?"). */
function monthMentioned(question: string): number | null {
  const text = normalize(question);
  const index = MONTH_NAMES.findIndex((name) => text.includes(name));
  return index >= 0 ? index : null;
}

function answerMonthlyResult(ctx: DemoContext, question: string): Answer {
  const monthly = monthlyProfitAndLoss(ctx.finance, ctx.period).filter((m) => safe(m.revenue) > 0);
  const calls = [tool('get_pnl_monthly', 'Estado de resultados mes a mes', periodInput(ctx))];

  if (monthly.length === 0) {
    return {
      reply: 'No hay meses con ventas en el periodo seleccionado. Amplía el periodo desde el selector de arriba.',
      toolCalls: calls,
    };
  }

  const parts: string[] = ['Lo que te dejó el negocio cada mes:'];
  parts.push(
    bullet(
      monthly.map(
        (m) =>
          `${capitalize(formatMonthLong(m.periodStart.slice(0, 7)))}: **${formatMoney(m.ebitda)}** (${formatPct(m.ebitdaPct)} de ${formatMoney(m.revenue)} que vendiste)`,
      ),
    ),
  );

  if (monthly.length >= 2) {
    // Si la pregunta nombra un mes concreto, se compara ese mes con el
    // anterior; si no, los dos ultimos del periodo.
    const named = monthMentioned(question);
    const namedPos =
      named === null
        ? -1
        : monthly.findIndex((m) => Number(m.periodStart.slice(5, 7)) - 1 === named);
    const position = namedPos > 0 ? namedPos : monthly.length - 1;

    const last = monthly[position];
    const previous = monthly[position - 1];
    const diff = safe(last.ebitda) - safe(previous.ebitda);
    const revenueDiff = safe(last.revenue) - safe(previous.revenue);
    const materialDiff = safe(last.materialCost) - safe(previous.materialCost);
    const laborDiff = safe(last.laborCost) - safe(previous.laborCost);
    const opDiff = safe(last.operatingExpenses) - safe(previous.operatingExpenses);

    parts.push(
      `Entre ${formatMonthLong(previous.periodStart.slice(0, 7))} y ${formatMonthLong(last.periodStart.slice(0, 7))} lo que te dejó el negocio ${diff >= 0 ? 'subió' : 'bajó'} **${formatMoney(Math.abs(diff))}**. De dónde sale esa diferencia:`,
    );
    parts.push(
      bullet([
        `Ventas: ${revenueDiff >= 0 ? '+' : '−'}${formatMoney(Math.abs(revenueDiff))}`,
        `Materia prima: ${materialDiff >= 0 ? '+' : '−'}${formatMoney(Math.abs(materialDiff))}`,
        `Personal: ${laborDiff >= 0 ? '+' : '−'}${formatMoney(Math.abs(laborDiff))}`,
        `Gastos generales: ${opDiff >= 0 ? '+' : '−'}${formatMoney(Math.abs(opDiff))}`,
      ]),
    );
  }

  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* --- Delivery -------------------------------------------------------- */

function answerDelivery(ctx: DemoContext): Answer {
  const calls = [tool('get_menu_pricing', 'Análisis de precios de toda la carta')];
  const rows = ctx.pricing.filter((row) => row.analysis.platformId);

  if (rows.length === 0) {
    return {
      reply:
        'No tienes ningún plato de la carta asignado a una plataforma de delivery, así que no puedo comparar márgenes. Puedes crear la versión de plataforma de un plato desde **Precios de la carta**.',
      toolCalls: calls,
    };
  }

  const parts: string[] = ['Así queda cada plato una vez descontada la comisión de la plataforma:'];
  parts.push(
    bullet(
      rows.map(({ analysis }) => {
        const margin = safe(analysis.grossMargin);
        return `**${analysis.name}** (${analysis.platformName ?? 'plataforma'}): ${formatMoney(analysis.sellingPriceGross)} de precio, ${formatMoney(analysis.platformCommission)} de comisión y ${formatMoney(analysis.costPerPortion)} de costo → margen de **${formatMoney(margin)}** (${formatPct(analysis.grossMarginPct)}).`;
      }),
    ),
  );

  const losing = rows.filter((row) => safe(row.analysis.grossMargin) < 0);
  const minMargin = safe(ctx.data.settings.alerts.minGrossMarginPct);
  const thin = rows.filter(
    (row) =>
      safe(row.analysis.grossMargin) >= 0 && safe(row.analysis.grossMarginPct) < minMargin,
  );

  if (losing.length > 0) {
    parts.push(
      `**${losing.length} ${losing.length === 1 ? 'plato pierde' : 'platos pierden'} dinero en plataforma.** Cada vez que se vende, el restaurante pone dinero. O subes el precio en la plataforma o lo sacas de ahí.`,
    );
  } else if (thin.length > 0) {
    parts.push(
      `Ninguno pierde dinero, pero ${thin.length} ${thin.length === 1 ? 'queda' : 'quedan'} por debajo del margen mínimo que te has fijado (${formatPct(minMargin)}).`,
    );
  } else {
    parts.push('Todos los platos en plataforma aguantan tu margen mínimo.');
  }

  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* --- Platos de la carta ---------------------------------------------- */

function answerDishes(ctx: DemoContext): Answer {
  const calls = [tool('get_menu_pricing', 'Análisis de precios de toda la carta')];
  const ranking = menuEngineering(
    ctx.pricing.map(({ item, analysis }) => ({ item, analysis })),
  );

  if (ranking.length === 0) {
    return {
      reply:
        'Todavía no tengo unidades vendidas por plato, así que no puedo ordenar la carta por lo que aporta cada uno. Anota las unidades vendidas en **Precios de la carta** y vuelve a preguntarme.',
      toolCalls: calls,
    };
  }

  const parts: string[] = ['Ordenados por lo que aporta cada plato al negocio en el periodo:'];
  parts.push(
    bullet(
      ranking
        .slice(0, 5)
        .map(
          (row) =>
            `**${row.name}**: ${formatMoney(row.totalContribution)} de contribución (${row.unitsSold} unidades × ${formatMoney(row.contributionMargin)} de margen) — ${row.classification}.`,
        ),
    ),
  );

  const worst = ranking[ranking.length - 1];
  if (ranking.length > 1) {
    parts.push(
      `El que menos aporta es **${worst.name}**, con ${formatMoney(worst.totalContribution)}. ${worst.recommendation}`,
    );
  }

  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* --- Precios y costeo ------------------------------------------------- */

function answerPricing(ctx: DemoContext): Answer {
  const calls = [tool('get_menu_pricing', 'Análisis de precios de toda la carta')];
  const rows = ctx.pricing.filter((row) => !row.analysis.platformId);

  if (rows.length === 0) {
    return {
      reply: 'No tengo ningún plato de la carta con costeo completo para calcular su precio.',
      toolCalls: calls,
    };
  }

  const parts: string[] = ['Costo y precio de cada plato en sala:'];
  parts.push(
    bullet(
      rows.map(
        ({ analysis }) =>
          `**${analysis.name}**: cuesta ${formatMoney(analysis.costPerPortion)} por porción y lo vendes a ${formatMoney(analysis.sellingPriceGross)} — costo de materia prima ${formatPct(analysis.foodCostPct)}, ${analysis.priceUnreachable ? 'con esa comisión no llegas a tu mínimo a ningún precio' : `precio sugerido ${formatMoney(analysis.suggestedPriceGross)}`}.`,
      ),
    ),
  );

  const under = rows.filter((row) => row.analysis.status === 'infraprecio');
  parts.push(
    under.length > 0
      ? `**${under.length} ${under.length === 1 ? 'plato está' : 'platos están'} por debajo de tu objetivo de costo de materia prima.** Ahí tienes el margen más fácil de recuperar.`
      : 'Todos los platos están dentro del objetivo de costo que te has fijado.',
  );

  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* --- Dias de la semana ------------------------------------------------ */

function answerWeekday(ctx: DemoContext): Answer {
  const calls = [tool('get_weekday', 'Rendimiento por día de la semana', periodInput(ctx))];
  const rows = weekdayPerformance(ctx.data.revenues, ctx.period).filter((r) => safe(r.days) > 0);

  if (rows.length === 0) {
    return {
      reply: 'No hay ventas registradas en el periodo, así que no puedo comparar los días de la semana.',
      toolCalls: calls,
    };
  }

  const sorted = [...rows].sort((a, b) => safe(b.revenue) - safe(a.revenue));
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];

  const parts: string[] = ['Ventas acumuladas por día de la semana en el periodo:'];
  parts.push(
    bullet(
      sorted.map(
        (row) =>
          `**${capitalize(row.label)}**: ${formatMoney(row.revenue)} en ${row.days} días, ticket promedio de ${formatMoney(row.averageTicket)}.`,
      ),
    ),
  );
  parts.push(
    `El mejor día es el **${best.label}** y el más flojo el **${worst.label}**, con ${formatMoney(worst.revenue)}. Antes de decidir si cierras ese día, mira qué costos te ahorrarías de verdad: el alquiler y los sueldos fijos siguen ahí.`,
  );

  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* --- Resumen general --------------------------------------------------- */

function answerOverview(ctx: DemoContext): Answer {
  const kpis = computeKpis(ctx.finance, ctx.period, ctx.data.settings);
  const pnl = computeProfitAndLoss(ctx.finance, ctx.period);
  const benchmarks = benchmarkKpis(kpis);
  const calls = [
    tool('get_kpis', 'KPIs del periodo frente a tus objetivos', periodInput(ctx)),
    tool('get_pnl', 'Estado de resultados del periodo', periodInput(ctx)),
  ];

  if (safe(kpis.revenue) <= 0) {
    return {
      reply: 'No hay ingresos registrados en este periodo. Amplía el periodo o registra tus ventas para que pueda contarte algo.',
      toolCalls: calls,
    };
  }

  const parts: string[] = [
    `En el periodo vendiste **${formatMoney(pnl.revenue)}** y el negocio te dejó **${formatMoney(pnl.ebitda)}** (${formatPct(pnl.ebitdaPct)}). Después del desgaste de tus equipos (${formatMoney(pnl.depreciation)}), los intereses y los impuestos, tu ganancia neta es de ${formatMoney(pnl.netProfit)}.`,
  ];

  const goals = [
    { label: 'Materia prima', actual: kpis.foodCostPct, target: ctx.target?.foodCostPct, lower: true },
    { label: 'Personal', actual: kpis.laborCostPct, target: ctx.target?.laborCostPct, lower: true },
    { label: 'Materia prima + personal', actual: kpis.primeCostPct, target: ctx.target?.primeCostPct, lower: true },
    { label: 'Te dejó el negocio', actual: kpis.ebitdaPct, target: ctx.target?.ebitdaPct, lower: false },
    { label: 'Cuánto pueden bajar tus ventas', actual: kpis.safetyMarginPct, target: ctx.target?.safetyMarginPct, lower: false },
  ].filter((goal) => goal.target != null);

  if (goals.length > 0) {
    parts.push('Tus indicadores frente a los objetivos que has configurado:');
    parts.push(
      bullet(
        goals.map((goal) => {
          const met = goal.lower ? goal.actual <= goal.target! : goal.actual >= goal.target!;
          return `**${goal.label}**: ${formatPct(goal.actual)} frente a ${formatPct(goal.target!)} — ${met ? 'dentro del objetivo' : 'fuera del objetivo'}.`;
        }),
      ),
    );
  } else {
    parts.push('No tienes objetivos configurados. Como referencia general del sector:');
    parts.push(
      bullet(
        benchmarks.map(
          (b) =>
            `**${ETIQUETA_DE_DUENO[b.label] ?? b.label}**: ${formatPct(b.value)} — ${b.status === 'bien' ? 'dentro del rango' : b.status === 'atencion' ? 'conviene vigilarlo' : 'fuera del rango'}.`,
        ),
      ),
    );
  }
  parts.push(
    `Necesitas vender ${formatMoney(kpis.breakEvenRevenue)} para no perder; con lo que llevas, vendiste un ${formatPct(kpis.safetyMarginPct)} por encima. Cada cliente gasta ${formatMoney(kpis.averageTicketPerCustomer)}.`,
  );

  return { reply: parts.join('\n\n'), toolCalls: calls };
}

/* ------------------------------------------------------------------ */
/* Enrutado por intencion                                              */
/* ------------------------------------------------------------------ */

interface Intent {
  id: string;
  keywords: string[];
  answer: (ctx: DemoContext, question: string) => Answer;
}

const INTENTS: Intent[] = [
  {
    id: 'payables',
    keywords: ['factura', 'vencid', 'proveedor', 'debo', 'deuda', 'pagar', 'por pagar'],
    answer: answerPayables,
  },
  {
    id: 'cash',
    keywords: ['caja', 'tesoreria', 'liquidez', 'saldo', 'dinero disponible', 'banco'],
    answer: answerCash,
  },
  {
    id: 'foodcost',
    keywords: ['materia prima', 'food cost', 'foodcost', 'compras', 'escandallo', 'recostear', 'desviacion'],
    answer: answerFoodCost,
  },
  {
    id: 'monthly',
    keywords: ['ebitda', 'resultado', 'beneficio', 'mes a mes', 'bajo', 'subio', 'perdidas', 'gano', 'dejo menos', 'dejo el negocio', 'desgaste'],
    answer: answerMonthlyResult,
  },
  {
    id: 'delivery',
    keywords: ['delivery', 'plataforma', 'glovo', 'uber', 'just eat', 'reparto', 'domicilio', 'comision'],
    answer: answerDelivery,
  },
  {
    id: 'dishes',
    keywords: ['plato', 'carta', 'menu', 'mas dinero', 'rentable', 'estrella', 'vende'],
    answer: answerDishes,
  },
  {
    id: 'pricing',
    keywords: ['precio', 'cuanto cuesta', 'cuanto me cuesta', 'subir el precio', 'cobrar'],
    answer: answerPricing,
  },
  {
    id: 'weekday',
    keywords: ['dia de la semana', 'lunes', 'martes', 'domingo', 'abrir', 'cerrar', 'semana'],
    answer: answerWeekday,
  },
  {
    id: 'overview',
    keywords: ['como va', 'que tal va', 'resumen', 'negocio', 'general', 'kpi', 'indicador'],
    answer: answerOverview,
  },
];

/** Lo que Alexia sabe responder con los datos de ejemplo. */
const DEMO_TOPICS = [
  'facturas vencidas y lo que debes a proveedores',
  'cómo va tu caja y si vas a tener problemas los próximos meses',
  'tu costo de materia prima y qué platos hay que recostear',
  'por qué ha subido o bajado el resultado mes a mes',
  'qué platos ganan o pierden dinero, en sala y en delivery',
  'qué días de la semana te compensan',
];

function fallbackAnswer(): Answer {
  return {
    reply: [
      'Esta es la versión de demostración de Alexia: **respondo a un conjunto limitado de preguntas** y esta no la he reconocido. No me invento cifras, así que prefiero decírtelo antes que darte un número que no sale de tus datos.',
      'Con los datos de ejemplo puedo contarte:',
      bullet(DEMO_TOPICS),
      'Prueba con una de las preguntas sugeridas o reformula la tuya con alguna de esas palabras.',
    ].join('\n\n'),
    toolCalls: [],
  };
}

/** Elige la intencion con mas palabras clave en comun con la pregunta. */
function matchIntent(question: string): Intent | null {
  const text = normalize(question);
  let best: Intent | null = null;
  let bestScore = 0;

  for (const intent of INTENTS) {
    const score = intent.keywords.reduce(
      (total, keyword) => (text.includes(keyword) ? total + keyword.length : total),
      0,
    );
    if (score > bestScore) {
      best = intent;
      bestScore = score;
    }
  }

  return bestScore > 0 ? best : null;
}

/**
 * «¿Qué es «Materia prima»?» (el «Pregúntale a Alexia» de cada «?», 15 oct
 * 2026): la demo contesta como Alexia, con el texto del «?» tal cual.
 */
function answerQueEs(question: string): Answer | null {
  const q = question.trim();
  if (!/^¿\s*qu[eé] (es|significa)\b/i.test(q)) return null;
  // «¿Qué es «X»?» o «¿Qué es el X?»: sin signos, para comparar.
  const entre = /«(.+)»/.exec(q)?.[1] ?? q.replace(/^¿\s*qu[eé] (es|significa)\s+(el |la |los |las )?/i, '');
  const limpia = (t: string) => normalize(t).replace(/[¿?«».]/g, '').trim();
  const buscada = limpia(entre);
  const entrada = Object.values(GLOSARIO).find((e) => [e.palabra, ...(e.tambien ?? []), e.sigla ?? ''].some((p) => p !== '' && limpia(p) === buscada));
  return entrada ? { reply: entrada.texto, toolCalls: [] } : null;
}

export function demoChat(
  data: DemoAssistantData,
  question: string,
  period: Period,
  today: string,
): AssistantChatResponse {
  const queEs = answerQueEs(question);
  if (queEs) return { reply: queEs.reply, toolCalls: queEs.toolCalls, usage: { inputTokens: 0, outputTokens: 0 } };
  const intent = matchIntent(question);
  const answer = intent
    ? intent.answer(buildDemoContext(data, period, today), question)
    : fallbackAnswer();
  return {
    reply: answer.reply,
    toolCalls: answer.toolCalls,
    usage: { inputTokens: 0, outputTokens: 0 },
  };
}
